import { FormikProps } from 'formik';

import { IUser } from '@modules/admin/AdminTools/Users/interfaces';
import { IDealSummary } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';

import { EStipTypeEntity } from '@shared/interfaces';
import { OrderByDirectionEnum } from '@shared/hooks/useServerSide';

import { ILead } from '../Tabs/components/Leads/types/interfaces/interfaces';
import { IAsset } from '../Tabs/components/Assets/interfaces';
import { IReminderModel } from './components/AddReminderModal';
import { ICreditReport } from './components/CreditReport/interfaces/interfaces';

export interface IContactHeaderCreditScore {
  bureauType: string;
  storageFileId: string;
  fico: number;
  scoreType: string;
  dti: number;
  isHtmlReport: boolean;
  creditScore: ICreditScore[];
  firstName: string;
  lastName: string;
}

export interface ICreditScore {
  bureauType: string;
  dti: string;
  fico: string;
  isHtmlReport: boolean;
  scoreType: string;
}

export interface IContactHeaderData {
  firstName: string;
  middleInitial: string;
  lastName: string;
  cellPhone: string;
  homePhone: string;
  workPhone: string;
  email: string;
  workEmail: string;
  militaryStatus: string;
  maritalStatus: string;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  unit: string;
  zip: string;
  county: string;
  creditScore: IContactHeaderCreditScore[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentLocalTime: string | null;
  localTimeZone: string | null;
  suffix: string;
  isMilitaryLendingActAvailable: boolean;
  numericId: number;
}

export interface IStepReason {
  id: string;
  reasonId?: string;
  numericId: number;
  index: number;
  label: string;
  description: string | null;
  isResolved: boolean;
  lenderList: string[] | null;
  isAutoSelected: boolean;
  isLcCondition?: boolean | null;
}

export interface CommonReasons {
  reasonId?: string;
  id?: string;
  label: string;
  description: string | null;
  isResolved: boolean;
  isResolvedLc?: boolean;
  isResolvedLp?: boolean;
  isLcCondition?: boolean | null;
}

export interface INextStep {
  workflowStepId: string;
  workflowStepNumericId: number;
  workflowStepName: string;
  workflowId: string;
  displayName: string;
  description: string;
  stepType: string;
  textCommentColor: string | null;
  isTextBold: boolean;
  roleId: string;
  reasons: IStepReason[];
}

export interface IStep {
  workflowStepId: string;
  workflowStepNumericId: number;
  workflowStepName: string;
  workflowId: string;
  workflowNumericId: number;
  workflowName: string;
  createdAt: string;
  days: number;
  description: string | null;
  reasons: IStepReason[];
  nextSteps: INextStep[];
  reasonLenderMapping?: IReasonLenderMapping[] | null;
  isStepCondition?: boolean;
  showNextStep?: boolean;
}

export interface ISkipStep {
  workflowStepName: string;
  workFlowStepMessage: string;
}

export interface IWorkflow {
  steps: IStep[];
  reasons?: CommonReasons[];
  leadId?: string;
}

export type TDealWorkflowDetailsProps = {
  leadId: string;
  dealId: string;
  currentTab: number;
};

export enum TabIndexEnum {
  'Contact',
  'Assets',
  'Leads',
  'Deals',
  'Files',
}

export interface IContactManagerHeaderProps {
  isLoading: boolean;
  contactId: string;
  leadId: string;
  dealId: string;
  assetId: string;
  currentTab: number;
  contactDetailsData: IContactHeaderData | undefined;
  leadData: ILeadData | undefined;
  leads: ILead[];
  dealSummary: IDealSummary[] | [];
  assets: IAsset[];
  onOpenCreditReport: (bureauType: EBureauType, pulledFicoTypes: CreditBureauScoreType[]) => void;
  disableLeftBtn?: boolean;
  disableRightBtn?: boolean;
  onArrowClick: (isLeftClicked?: boolean) => void;
}

export interface User {
  userId: number;
  name: string;
}

export interface Lead {
  leadId: string;
  assetId: string;
  leadNumericId: number;
  sourceName: string;
  secondarySourceName: string;
  loanApplicationId: string;
  pickedNumericDealId: number;
  pickedDealId: string;
  user: User;
}

export interface LienHolderAddress {
  street: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  years: string;
  months: string;
  monthlyPayment: number;
  county: string;
}

export interface IAssetLienHolderResponse {
  lienAmount: number | null;
}

export interface Asset {
  assetId: string;
  assetNumericId: number;
  assetType: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  mileage: number;
  trim: string;
  lienAmount: number;
  lienMonthlyPayment: number;
  lienRate: number;
  lienPerDiem: number;
  lienGoodDate: Date;
  lienHolderName: string;
  lienHolderId: string;
  nadaValuation: number;
  nadaBookoutDate: Date;
  kbbValuation: number;
  kbbBookoutDate: Date;
  msrpValuation: number;
  tradeInValuation: number;
  kbbWholeSaleValuation: number;
  kbbFinanceExpressValuation: number;
  nadaTradeInValuation: number;
  insuranceCompanyName: string;
  insurancePhone: string;
  insurancePolicyNumber: string;
  insuranceEffectiveDate: Date;
  insuranceExpirationDate: Date;
  lienPhoneNumber: string;
  lienAccountNumber: string;
  lienHolderAddress: LienHolderAddress;
  registrationState: string;
  registrationNumberOfOwners: number;
  registrationExpirationDate: Date;
  registrationOwnerName1: string;
  registrationOwnerName2: string;
  registrationOwnerNameChange1: boolean;
  registrationOwnerNameChange2: boolean;
  registrationNumber: string;
}

export interface ILeadData {
  lead: Lead;
  asset: Asset;
}

export interface UpdatedBy {
  id: string;
  email: string;
  name: string;
}

export interface IStateData {
  id: string;
  state: string;
  titleHoldingStatus: boolean | null;
  sorRequiredStatus: boolean | null;
  addStatus: boolean | null;
  dropStatus: boolean | null;
  notaryRequiredStatus: boolean | null;
  titleDocuments: string | null;
  salesTaxInformation: string | null;
  specificRequirements: string | null;
  isAllowProductAndSalesTax: boolean;
  isOutOfStateTitleAllowed: boolean | null;
  updatedBy: UpdatedBy | null;
  updatedDate: string;
}

export interface IStipsRequestListItem {
  id: string;
  stipTypeName: string | null;
  stipTypeId: string | null;
  additionalNote: string | null;
  relatedToEntity: EStipTypeEntity;
  receivedDate: string | null;
  requestedDate: string | null;
  description: string | null;
  contactId: string | null;
  dealId: string | null;
  assetId: string | null;
  review: string | null;
  isCoBorrower?: boolean;
}

export interface IStipsRequest {
  contactId: string;
  assetId: string;
  coBorrowerId: string | null;
  note: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  coBorrowerEmail: string | null;
  coBorrowerPhone: string | null;
  stipsRequestItems: IStipsRequestListItem[];
}

type IStipsRequestPayloadListItem = Omit<
  IStipsRequestListItem,
  'stipTypeName' | 'relatedToEntity' | 'receivedDate' | 'requestedDate' | 'review' | 'description'
>;

export interface IStipsRequestPayload {
  contactId: string;
  assetId: string;
  coBorrowerId: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  coBorrowerEmail: string | null;
  coBorrowerPhone: string | null;
  note: string | null;
  contactSendEmail: boolean;
  contactSendText: boolean;
  coBorrowerSendEmail: boolean;
  coBorrowerSendText: boolean;
  stipRequestItems: IStipsRequestPayloadListItem[];
}

export interface IStateModalProps {
  stateValue: string;
  handleClose: () => void;
}

export interface ILeadNoAssetAssignedModalProps {
  messageText: string;
  handleClose: () => void;
}

export type ConfigDataType = {
  label: string;
  value: boolean | null;
};

export interface IReminderFormProps extends Partial<FormikProps<IReminderModel>> {
  deals?: IDealSummary[] | [];
  users: IUser[];
  handleClose: () => void;
}

export interface IReasonLenderMapping {
  id: string;
  label: string;
  description: string | null;
  isEnabled: boolean;
  lenderGuid: string;
}

export interface IReasonInitalStatus {
  label: string;
  isChecked: boolean;
}

export interface IDealData {
  dealId: string;
  numericId: number;
  isPicked: boolean;
}

export interface IDealResponse {
  data: IDealData[];
}

export interface ICreditReportData {
  creditReportSummary: ICreditReport;
  canPullCreditReport: boolean;
  canViewCreditReport: boolean;
  isHardPullReport: boolean;
  message: string | null;
}

export interface IQueueDataQueryParams {
  queueId: string;
  pageSize: number;
  offSet: number;
  viewAsUserId: string;
  orderBy?: string;
  orderByDirection?: OrderByDirectionEnum;
}

export enum EBureauType {
  Equifax = 'Equifax',
  Transunion = 'Transunion',
  Experian = 'Experian',
  ExperianFico = 'ExperianFico',
}

export enum CreditBureauScoreType {
  // Equifax
  // Eqf = 1,
  EqfV9 = 1,
  EqfV4 = 13,
  EqfV5Auto = 14,
  EqfFicoV8 = 23,
  //Experian
  ExpV2 = 8,
  ExpV2Auto = 3, //Not Available from CBC
  ExpFicoV3 = 9,
  ExpV4 = 7,
  ExpV8 = 4, //Not Available from CBC
  ExpV8Auto = 10,
  ExpV9 = 11,
  ExpV9Auto = 12,
  // Transunion
  TuV3 = 18,
  TuV4 = 6,
  TuV8Auto = 19,
  TuFicoV8 = 24,
  TuFicoV9 = 21,
}

//BE to UI score type reverse mapping
export enum CreditBureauScoreTypeMap {
  //Equifax
  Eqf = 'EqfV9',
  EQFV4 = 'EqfV4',
  EQFV5AUTO = 'EqfV5Auto',
  EqfFicoV8 = 'EqfFicoV8',

  //Experian
  V2 = 'ExpV2',
  ExpV2Auto = 'ExpV2Auto',
  V3 = 'ExpFicoV3',
  ExpV4 = 'ExpV4',
  ExpV8 = 'ExpV8',
  V8A = 'ExpV8Auto',
  V9 = 'ExpV9',
  V9A = 'ExpV9Auto',

  //Transunion
  TuV3 = 'TuV3',
  Tu = 'TuV4',
  TuV8Auto = 'TuV8Auto',
  TuFicoV8 = 'TuFicoV8',
  TuFicoV9 = 'TuFicoV9',
}

export interface ScoreMetadata {
  label: string;
  bureau: EBureauType;
}

// Preserves insertion order to show in UI dropdowns
export const CreditBureauScoreMetadata = new Map<CreditBureauScoreType, ScoreMetadata>([
  // Equifax
  [CreditBureauScoreType.EqfV9, { label: 'EqfV9', bureau: EBureauType.Equifax }],
  [CreditBureauScoreType.EqfV4, { label: 'EqfV4', bureau: EBureauType.Equifax }],
  [CreditBureauScoreType.EqfV5Auto, { label: 'EqfV5Auto', bureau: EBureauType.Equifax }],
  [CreditBureauScoreType.EqfFicoV8, { label: 'EqfFicoV8', bureau: EBureauType.Equifax }],

  // Experian
  [CreditBureauScoreType.ExpV2, { label: 'ExpV2', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpV2Auto, { label: 'ExpV2Auto', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpFicoV3, { label: 'ExpFicoV3', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpV4, { label: 'ExpV4', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpV8, { label: 'ExpV8', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpV8Auto, { label: 'ExpV8Auto', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpV9, { label: 'ExpV9', bureau: EBureauType.Experian }],
  [CreditBureauScoreType.ExpV9Auto, { label: 'ExpV9Auto', bureau: EBureauType.Experian }],

  // Transunion
  [CreditBureauScoreType.TuV3, { label: 'TuV3', bureau: EBureauType.Transunion }],
  [CreditBureauScoreType.TuV4, { label: 'TuV4', bureau: EBureauType.Transunion }],
  [CreditBureauScoreType.TuV8Auto, { label: 'TuV8Auto', bureau: EBureauType.Transunion }],
  [CreditBureauScoreType.TuFicoV8, { label: 'TuFicoV8', bureau: EBureauType.Transunion }],
  [CreditBureauScoreType.TuFicoV9, { label: 'TuFicoV9', bureau: EBureauType.Transunion }],
]);

export const labelToMapKey: Record<string, keyof typeof CreditBureauScoreTypeMap> = 
  Object.fromEntries(
    Object.entries(CreditBureauScoreTypeMap).map(([k, v]) => [v, k as keyof typeof CreditBureauScoreTypeMap])
);

export const getScoreDetailsByBureau = (bureau?: EBureauType) => {
  return Array.from(CreditBureauScoreMetadata.entries())
    .map(([id, meta]) => ({
      id,
      label: meta.label,
      bureau: meta.bureau,
      mappedValue: labelToMapKey[meta.label],
    }))
    .filter((item) => !bureau || item.bureau === bureau);
};
